import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bot, Database, Send, Sparkles, User } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { chatApi, chatKeys } from '@/features/chat/api';
import { useChatHistory } from '@/features/chat/hooks';
import type { ChatTurn } from '@/features/chat/types';

interface LocalMessage {
  role: 'user' | 'assistant';
  content: string;
  sources?: string[];
}

const SUGGESTED_QUESTIONS = [
  'What datasets are connected to the platform?',
  'Which tables have the most columns?',
  'What data quality anomalies have been detected?',
  'Summarise the schema of the first connected dataset.',
];

export function ChatPage(): JSX.Element {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<LocalMessage[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const history = useChatHistory();
  const queryClient = useQueryClient();

  // Seed messages from DB on first load
  useEffect(() => {
    if (!history.data || historyLoaded) return;
    if (history.data.length > 0) {
      const loaded: LocalMessage[] = [...history.data]
        .reverse()
        .flatMap((msg) => [
          { role: 'user' as const, content: msg.question },
          { role: 'assistant' as const, content: msg.answer, sources: msg.sources ?? [] },
        ]);
      setMessages(loaded);
    }
    setHistoryLoaded(true);
  }, [history.data, historyLoaded]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming]);

  function buildHistory(): ChatTurn[] {
    return messages.slice(-6).map((m) => ({ role: m.role, content: m.content }));
  }

  async function handleSend(question: string): Promise<void> {
    const q = question.trim();
    if (!q || isStreaming) return;

    const history = buildHistory();
    setMessages((prev) => [...prev, { role: 'user', content: q }]);
    setInput('');
    setIsStreaming(true);
    // No empty placeholder — ThinkingBubble covers the waiting state until the
    // first token arrives, at which point we insert the assistant message.

    let fullAnswer = '';
    let sources: string[] = [];
    let requestId: string | null = null;
    let assistantAdded = false;

    try {
      for await (const event of chatApi.stream(q, history)) {
        if (event.error) {
          throw new Error(event.error);
        }
        if (event.token) {
          fullAnswer += event.token;
          const isFirst = !assistantAdded;
          assistantAdded = true;
          // flushSync forces React to paint each token immediately instead of
          // batching all of them into a single update (React 18 auto-batching).
          flushSync(() => {
            setMessages((prev) => {
              if (isFirst) {
                return [...prev, { role: 'assistant', content: fullAnswer, sources: [] }];
              }
              const next = [...prev];
              next[next.length - 1] = { role: 'assistant', content: fullAnswer, sources: [] };
              return next;
            });
          });
        }
        if (event.done) {
          sources = event.sources ?? [];
          requestId = event.request_id ?? null;
          setMessages((prev) => {
            if (!assistantAdded) {
              assistantAdded = true;
              return [...prev, { role: 'assistant', content: fullAnswer, sources }];
            }
            const next = [...prev];
            next[next.length - 1] = { role: 'assistant', content: fullAnswer, sources };
            return next;
          });
        }
      }

      // Persist to DB after stream completes
      if (fullAnswer) {
        await chatApi.save(q, fullAnswer, sources, requestId);
        queryClient.invalidateQueries({ queryKey: chatKeys.history() });
      }
    } catch (err) {
      console.error('[chat stream error]', err);
      const errorMsg: LocalMessage = {
        role: 'assistant',
        content: 'Sorry, I ran into an error. Please try again.',
        sources: [],
      };
      setMessages((prev) => {
        if (assistantAdded) {
          const next = [...prev];
          next[next.length - 1] = errorMsg;
          return next;
        }
        return [...prev, errorMsg];
      });
    } finally {
      setIsStreaming(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(input);
    }
  }

  const isLoading = history.isPending && !historyLoaded;
  const isEmpty = !isLoading && messages.length === 0 && !isStreaming;
  // Show the thinking animation only until the first token arrives (the
  // assistant placeholder still has empty content).
  const lastMsg = messages[messages.length - 1];
  const showThinking = isStreaming && !(lastMsg?.role === 'assistant' && lastMsg.content);

  return (
    <div className="flex h-full flex-col">
      {/* Page header */}
      <div className="shrink-0 border-b border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 px-8 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-50 dark:bg-brand-900/30 text-brand-600">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-ink-900 dark:text-ink-50">AI Assistant</h1>
            <p className="text-xs text-ink-500 dark:text-ink-400">
              Ask questions about your connected datasets, schemas, and data quality.
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable messages area */}
      <div className="flex-1 overflow-y-auto px-8 py-6 space-y-5 bg-ink-50 dark:bg-ink-950">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-ink-200 dark:border-white/10 border-t-brand-500" />
          </div>
        ) : isEmpty ? (
          <EmptyState onSuggest={handleSend} />
        ) : (
          <>
            {messages.map((msg, i) => (
              <MessageBubble key={i} message={msg} />
            ))}
            {showThinking && <ThinkingBubble />}
          </>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Pinned input bar */}
      <div className="shrink-0 border-t border-ink-200/70 dark:border-white/10 bg-white dark:bg-ink-900 px-8 py-4">
        <div className="mx-auto max-w-3xl flex items-end gap-3">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your data… (Enter to send, Shift+Enter for newline)"
            rows={1}
            className="flex-1 resize-none rounded-xl border border-ink-200 dark:border-white/10 bg-ink-50 dark:bg-ink-800 px-4 py-3 text-sm text-ink-900 dark:text-ink-50 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-brand-400 max-h-40 overflow-y-auto"
            style={{ fieldSizing: 'content' } as React.CSSProperties}
          />
          <button
            type="button"
            onClick={() => handleSend(input)}
            disabled={!input.trim() || isStreaming}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-600 text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isStreaming ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onSuggest }: { onSuggest: (q: string) => void }): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-50 dark:bg-brand-900/30 text-brand-600">
        <Sparkles className="h-8 w-8" />
      </div>
      <h3 className="mt-4 text-base font-semibold text-ink-900 dark:text-ink-50">
        Ask anything about your data
      </h3>
      <p className="mt-1.5 max-w-sm text-sm text-ink-500 dark:text-ink-400">
        I can answer questions about connected datasets, column schemas, data quality, and more.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-2 max-w-lg">
        {SUGGESTED_QUESTIONS.map((q) => (
          <button
            key={q}
            type="button"
            onClick={() => onSuggest(q)}
            className="rounded-full border border-ink-200 dark:border-white/10 bg-white dark:bg-ink-800 px-4 py-2 text-xs text-ink-700 dark:text-ink-300 transition hover:border-brand-400 hover:text-brand-600 dark:hover:text-brand-400"
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ message }: { message: LocalMessage }): JSX.Element {
  const isUser = message.role === 'user';
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''} max-w-3xl mx-auto w-full`}>
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          isUser
            ? 'bg-brand-600 text-white'
            : 'bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-300'
        }`}
      >
        {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>

      <div className={`min-w-0 flex-1 space-y-1.5 ${isUser ? 'items-end flex flex-col' : ''}`}>
        <div
          className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
            isUser
              ? 'rounded-tr-sm bg-brand-600 text-white max-w-[80%]'
              : 'rounded-tl-sm bg-white dark:bg-ink-800 text-ink-900 dark:text-ink-50 shadow-sm border border-ink-100 dark:border-white/5'
          }`}
        >
          {isUser ? (
            message.content
          ) : (
            <MarkdownContent content={message.content} />
          )}
        </div>

        {!isUser && message.sources && message.sources.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-1">
            {message.sources.map((s) => (
              <span
                key={s}
                className="inline-flex items-center gap-1 rounded-full border border-ink-200 dark:border-white/10 bg-white dark:bg-ink-900 px-2 py-0.5 text-[10px] font-medium text-ink-500 dark:text-ink-400"
              >
                <Database className="h-2.5 w-2.5" />
                {s}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MarkdownContent({ content }: { content: string }): JSX.Element {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
        ul: ({ children }) => <ul className="mb-2 ml-4 list-disc space-y-0.5">{children}</ul>,
        ol: ({ children }) => <ol className="mb-2 ml-4 list-decimal space-y-0.5">{children}</ol>,
        li: ({ children }) => <li>{children}</li>,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        code: ({ children, className }) => {
          const isBlock = className?.includes('language-');
          return isBlock ? (
            <code className="block rounded bg-ink-900/10 dark:bg-black/20 px-3 py-2 font-mono text-xs mt-1 mb-2 overflow-x-auto">
              {children}
            </code>
          ) : (
            <code className="rounded bg-ink-900/10 dark:bg-black/20 px-1 py-0.5 font-mono text-xs">
              {children}
            </code>
          );
        },
        pre: ({ children }) => <pre className="overflow-x-auto">{children}</pre>,
        h1: ({ children }) => <h1 className="text-base font-semibold mb-1">{children}</h1>,
        h2: ({ children }) => <h2 className="text-sm font-semibold mb-1">{children}</h2>,
        h3: ({ children }) => <h3 className="text-sm font-medium mb-1">{children}</h3>,
        a: ({ href, children }) => (
          <a href={href} className="underline underline-offset-2 hover:opacity-80" target="_blank" rel="noreferrer">
            {children}
          </a>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

function ThinkingBubble(): JSX.Element {
  return (
    <div className="flex gap-3 max-w-3xl mx-auto w-full">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink-100 dark:bg-ink-800 text-ink-600 dark:text-ink-300">
        <Bot className="h-4 w-4" />
      </div>
      <div className="rounded-2xl rounded-tl-sm bg-white dark:bg-ink-800 border border-ink-100 dark:border-white/5 shadow-sm px-4 py-3">
        <div className="flex gap-1">
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-ink-400 [animation-delay:0ms]" />
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-ink-400 [animation-delay:150ms]" />
          <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-ink-400 [animation-delay:300ms]" />
        </div>
      </div>
    </div>
  );
}
